/**
 * Accept Invite - where an admin-issued invite link lands.
 *
 * PUBLIC ROUTE. The visitor has no session yet; the token in the URL is
 * the only thing vouching for them, and it is spent by the submit below.
 * On success the server issues a session IDENTICAL in shape to a
 * Google-issued one, so the app is entered exactly as an OAuth user
 * enters it - via AuthContext's checkAuth(), the same canonical hydration
 * path AuthCallback uses.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';

// Mirrors passwords.py MIN_PASSWORD_LENGTH. This is a HINT shown before
// submitting, never the decision: the server rejects a short password
// regardless, and its message is what gets displayed on failure. Stated so
// nobody mistakes this for a second implementation of the rule.
const MIN_PASSWORD_LENGTH_HINT = 12;

// MODE-AWARE RATHER THAN DUPLICATED. An invite and a reset differ only in
// which endpoint spends the token and what the page calls itself; copying
// this component would create an unregistered mirror pair whose two halves
// could drift on validation, error handling or hydration. One component,
// two routes.
const COPY = {
  invite: {
    heading: 'Set your password',
    blurb: 'Choose a password to finish setting up your account.',
    submit: 'Set password and sign in',
    missingToken: 'This link is missing its invite token. Ask your administrator for a new one.',
    failed: 'Could not set your password. The link may have expired or already been used.',
    landing: '/about',
  },
  reset: {
    heading: 'Choose a new password',
    blurb: 'Setting a new password will sign you out everywhere else.',
    submit: 'Set new password and sign in',
    missingToken: 'This link is missing its reset token. Ask your administrator for a new one.',
    failed: 'Could not reset your password. The link may have expired or already been used.',
    landing: '/',
  },
};

const AcceptInvite = ({ mode = 'invite' }) => {
  const copy = COPY[mode] || COPY.invite;
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [token, setToken] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (!t) {
      setError(copy.missingToken);
      return;
    }
    setToken(t);
  }, [copy.missingToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('The two passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      if (mode === 'reset') {
        await api.resetPassword(token, password);
      } else {
        await api.acceptInvite(token, password);
      }
      // Hydrate through the same path the OAuth callback uses, so the two
      // methods cannot diverge on any field of user state.
      await checkAuth();
      navigate(copy.landing, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : copy.failed
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/brand/logo-full.svg" alt="ProcMatrix" className="h-16 mx-auto mb-2" />
          <p className="text-zinc-600">AI-Powered Procurement Platform</p>
        </div>

        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-8">
          <h2 className="text-xl font-semibold text-zinc-900 mb-2 text-center">
            {copy.heading}
          </h2>
          <p className="text-zinc-500 text-sm text-center mb-6">
            {copy.blurb}
          </p>

          {error && (
            <div
              className="mb-6 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
              role="alert"
              data-testid="accept-invite-error"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="accept-invite-form">
            <label className="block text-xs font-medium text-zinc-600 mb-1" htmlFor="new-password">
              Password
            </label>
            <Input
              id="new-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              data-testid="accept-invite-password"
            />
            <p className="text-xs text-zinc-400 mt-1 mb-4" data-testid="accept-invite-hint">
              At least {MIN_PASSWORD_LENGTH_HINT} characters. A few unrelated words works well &mdash;
              length matters more than symbols.
            </p>

            <label className="block text-xs font-medium text-zinc-600 mb-1" htmlFor="confirm-password">
              Confirm password
            </label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              data-testid="accept-invite-confirm"
            />

            <Button
              type="submit"
              disabled={submitting || !token}
              className="w-full mt-6 bg-[#0055FF] hover:bg-[#0044CC] text-white py-6 text-base"
              data-testid="accept-invite-submit"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : copy.submit}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
